import React from 'react';
import BackOfficeLayout from '../../components/backoffice/BackOfficeLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Shield, Check, X } from 'lucide-react';

const ROLES = [
  { id: 'admin', label: 'Super Admin' },
  { id: 'merchant_admin', label: 'Merchant Admin' },
  { id: 'manager', label: 'Manager' },
  { id: 'viewer', label: 'Viewer' },
];

const PERMISSIONS = [
  { id: 'dashboard', label: 'View Dashboard', roles: ['admin', 'merchant_admin', 'manager', 'viewer'] },
  { id: 'backoffice', label: 'Access Back Office', roles: ['admin'] },
  { id: 'merchant_mgmt', label: 'Merchant Management', roles: ['admin'] },
  { id: 'user_create', label: 'Create Users', roles: ['admin', 'merchant_admin'] },
  { id: 'user_edit', label: 'Edit Users', roles: ['admin', 'merchant_admin'] },
  { id: 'user_delete', label: 'Delete Users', roles: ['admin', 'merchant_admin'] },
  { id: 'user_suspend', label: 'Suspend / Activate Users', roles: ['admin', 'merchant_admin'] },
  { id: 'view_users', label: 'View Users', roles: ['admin', 'merchant_admin', 'manager'] },
  { id: 'audit_logs', label: 'View Audit Logs', roles: ['admin', 'merchant_admin'] },
  { id: 'kyc_review', label: 'KYC / KYB Review & Approve', roles: ['admin'] },
  { id: 'payments_create', label: 'Create Payments', roles: ['admin', 'merchant_admin', 'manager'] },
  { id: 'payments_view', label: 'View Payments & Transactions', roles: ['admin', 'merchant_admin', 'manager', 'viewer'] },
  { id: 'accounts', label: 'Manage Accounts', roles: ['admin', 'merchant_admin', 'manager'] },
  { id: 'accounts_view', label: 'View Accounts', roles: ['admin', 'merchant_admin', 'manager', 'viewer'] },
  { id: 'beneficiaries', label: 'Manage Beneficiaries', roles: ['admin', 'merchant_admin', 'manager'] },
  { id: 'beneficiaries_view', label: 'View Beneficiaries', roles: ['admin', 'merchant_admin', 'manager', 'viewer'] },
  { id: 'analytics', label: 'View Analytics & Reports', roles: ['admin', 'merchant_admin'] },
  { id: 'notifications', label: 'Send Broadcasts', roles: ['admin'] },
  { id: 'settings_platform', label: 'Platform Configuration', roles: ['admin'] },
  { id: 'settings_profile', label: 'Profile & Security Settings', roles: ['admin', 'merchant_admin', 'manager', 'viewer'] },
  { id: '2fa_manage', label: 'Enable / Disable 2FA', roles: ['admin', 'merchant_admin', 'manager', 'viewer'] },
  { id: 'api_info', label: 'View API Info', roles: ['admin', 'merchant_admin'] },
];

export default function BackOfficeUsersRoles() {
  return (
    <BackOfficeLayout>
      <div className="space-y-6">
        <div className="pb-4">
          <h1 className="text-2xl font-bold text-[#073B2E] flex items-center gap-2">
            <Shield className="w-7 h-7 text-[#0B6445]" />
            Roles & Permissions
          </h1>
          <p className="text-[#0E1B16]/70 mt-2">Platform role definitions and their associated permissions.</p>
        </div>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#073B2E]">Permission Matrix</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-[#DCE4E0] bg-[#F8FAF9]">
                    <th className="text-left py-3 px-4 font-semibold text-[#073B2E] min-w-[220px]">Permission</th>
                    {ROLES.map((r) => (
                      <th key={r.id} className="text-center py-3 px-3 font-semibold text-[#073B2E] min-w-[120px]">{r.label}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {PERMISSIONS.map((p, i) => (
                    <tr key={p.id} className={`border-b border-[#DCE4E0]/60 ${i % 2 === 0 ? 'bg-white' : 'bg-[#FAFCFB]'}`}>
                      <td className="py-2.5 px-4 text-[#0F172A]">{p.label}</td>
                      {ROLES.map((r) => (
                        <td key={r.id} className="text-center py-2.5 px-3">
                          {p.roles.includes(r.id) ? (
                            <Check className="w-4 h-4 text-[#0B6445] inline-block" />
                          ) : (
                            <X className="w-4 h-4 text-slate-300 inline-block" />
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-[#DCE4E0] shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-base text-[#073B2E]">Role Descriptions</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 sm:grid-cols-2">
              {[
                { role: 'Super Admin', color: 'bg-red-50 border-red-200', desc: 'Full platform access. Manages all merchants, users, KYC reviews, system configuration, and audit logs. Cannot be assigned to merchants.' },
                { role: 'Merchant Admin', color: 'bg-blue-50 border-blue-200', desc: 'Manages their own organisation, creates users, views audit logs for their company, initiates payments, and manages accounts and beneficiaries.' },
                { role: 'Manager', color: 'bg-amber-50 border-amber-200', desc: 'Operational role within a merchant organisation. Can view users, create payments, manage accounts and beneficiaries, but cannot access audit logs or create new users.' },
                { role: 'Viewer', color: 'bg-slate-50 border-slate-200', desc: 'Read-only access within their organisation. Can view dashboard, transactions, accounts, and beneficiaries but cannot make changes.' },
              ].map((r) => (
                <div key={r.role} className={`rounded-lg border p-4 ${r.color}`}>
                  <h3 className="font-semibold text-[#073B2E] mb-1">{r.role}</h3>
                  <p className="text-sm text-[#0E1B16]/70">{r.desc}</p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </BackOfficeLayout>
  );
}
